import { ref, onMounted, onUnmounted } from 'vue';

interface NotificationAction {
  action: string;
  title: string;
  icon?: string;
  url?: string;
}

interface Notification {
  id: string;
  title: string;
  body: string;
  icon?: string | null;
  url?: string | null;
  actions?: NotificationAction[] | null;
  isRead: boolean;
  createdAt: string | Date;
}

export const useNotifications = () => {
  const notifications = ref<Notification[]>([]);
  const unreadCount = ref(0);
  const loading = ref(false);
  const error = ref<unknown>(null);
  let intervalId: ReturnType<typeof setInterval> | null = null;

  const fetchNotifications = async () => {
    loading.value = true;
    try {
      console.log('Fetching notifications...');
      const response = await $fetch<{ notifications: Notification[]; unreadCount: number }>('/api/notifications');
      console.log('Response:', response);
      notifications.value = response.notifications;
      unreadCount.value = response.unreadCount;
    } catch (err: unknown) {
      error.value = err;
      console.error('Error fetching notifications:', err);
    } finally {
      loading.value = false;
    }
  };

  const markAsRead = async (notificationIds?: string[]) => {
    try {
      await $fetch('/api/notifications/mark-read', {
        method: 'POST',
        body: { notificationIds }
      });

      if (notificationIds) {
        notifications.value = notifications.value.map((n) =>
          notificationIds.includes(n.id) ? { ...n, isRead: true } : n
        );
        unreadCount.value = Math.max(0, unreadCount.value - notificationIds.length);
      } else {
        notifications.value = notifications.value.map((n) => ({ ...n, isRead: true }));
        unreadCount.value = 0;
      }
    } catch (err: unknown) {
      console.error('Error marking notifications as read:', err);
    }
  };

  onMounted(() => {
    fetchNotifications();
    intervalId = setInterval(fetchNotifications, 60000);
  });

  onUnmounted(() => {
    if (intervalId) clearInterval(intervalId);
  });

  return {
    notifications,
    unreadCount,
    loading,
    error,
    fetchNotifications,
    markAsRead
  };
};
